import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { usePermissions } from "../context/PermissionContext";

/**
 * Route-level guard.
 *
 * The original app decided access with inline `user?.role === "Admin"` checks
 * around each <Route>. Here a route declares the module it belongs to and the
 * decision comes from the server-issued permission set, so adding a role or
 * moving a screen between roles needs no code change.
 *
 * This is a usability guard, not the security boundary - the API re-checks the
 * same matrix on every request.
 */
const ProtectedRoute = ({ module, permission = "VIEW", children }) => {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const { can, loading } = usePermissions();
  const location = useLocation();

  if (!isLoggedIn) {
    // Remember where they were headed so login can return them there.
    if (location.pathname && location.pathname !== "/") {
      sessionStorage.setItem("lastVisitedPage", location.pathname + location.search);
    }
    return <Navigate to="/" replace />;
  }

  // Permissions arrive a moment after login; don't bounce the user meanwhile.
  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-sky-600 rounded-full animate-spin" />
      </div>
    );
  }

  // No module declared means "any signed-in user" (profile, shared detail pages).
  if (module && !can(module, permission)) {
    return <Navigate to="/no-access" replace state={{ module, permission }} />;
  }

  return children;
};

export default ProtectedRoute;
